import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
// ✅ If using a toast system like react-hot-toast
import { toast } from "react-hot-toast";

export default function useAddMissingTransaction() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  const { send, isConnected, isOpen, connect } = useWebSocketContext();
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const logoutAndNotify = (msg) => {
    localStorage.removeItem("authToken");        
    toast.error(msg);                        
    setTimeout(() => {
      window.location.href = "/login";       
    }, 1200);
  };

  const addMissingTransaction = useCallback(async ({ token, groupID, roleID, amount, userID }) => {
    if (!mountedRef.current) return;

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      if (!isConnected) {
        if (!connectionAttemptedRef.current) {
          connectionAttemptedRef.current = true;
          await connect();
        }

        await new Promise((resolve, reject) => {
          const start = Date.now();
          const interval = setInterval(() => {
            if (!mountedRef.current) {
              clearInterval(interval);
              reject(new Error("Component unmounted"));
            } else if (isOpen()) {
              clearInterval(interval);
              resolve(true);
            } else if (Date.now() - start > 10000) {
              clearInterval(interval);
              reject(new Error("Connection timeout"));
            }
          }, 100);
        });
      }

      const response = await send("cms_add_wallet_missed_transaction", {
        token,
        group_id: groupID,
        role_id: roleID,
        amount,
        user_id: userID,
      });

      console.log("Add Missing Txn Response:", response);

      if (response?.message === "Invalid or expired token") {
        logoutAndNotify("Session expired. Please log in again.");
        return { success: false };
      }

      if (!response || response.type !== "cms_add_wallet_missed_transaction_response") {
        throw new Error("Invalid response from server");
      }

      if (!response.success) {
        throw new Error(response.message || "Request failed");
      }

      setMessage(response.message || "Transaction added successfully");
      return { success: true, message: response.message };

    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, isOpen, connect, send]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false };
  }, []);

  return { addMissingTransaction, isLoading, error, message };
}
